import React, {useEffect} from 'react';
import {FormattedMessage, injectIntl} from 'react-intl';
import messages from '../../../../res/translations/messages';
import {ModalBody, ModalFooter} from 'reactstrap';
import {Form} from 'formik';
import ScriptGeneratorClassifier2ChoicesForm from './ScriptGeneratorClassifier2ChoicesForm';
import {isEqual} from 'lodash';

const useTouchErrorsOnChange = (formik) => {
    const values = formik ? formik.values : null;
    const errors = formik ? formik.errors : null;
    const touched = formik ? formik.touched : null;
    const dirty = formik ? formik.dirty : null;
    const setTouched = formik ? formik.setTouched : null;

    useEffect(() => {
        if (!dirty || !errors || !setTouched) {
            return;
        }

        const derivedTouched = deriveTouchedFromErrors(errors, values);
        if (!derivedTouched) {
            return;
        }

        const nextTouched = mergeTouched(touched || {}, derivedTouched);
        if (!isEqual(nextTouched, touched)) {
            setTouched(nextTouched, false);
        }
    }, [dirty, errors, values, touched, setTouched]);
};

const deriveTouchedFromErrors = (node, values) => {
    if (!node) {
        return undefined;
    }

    if (Array.isArray(node)) {
        const result = [];
        node.forEach((entry, index) => {
            const child = deriveTouchedFromErrors(entry, values && values[index]);
            if (child !== undefined) {
                result[index] = child;
            }
        });
        return result.length ? result : undefined;
    }

    if (typeof node === 'object') {
        if (node === null) {
            return undefined;
        }
        if (node.key !== undefined) {
            // Don't collapse array-valued fields to `true` — Formik's setIn
            // crashes when it tries to set a nested path on a boolean.
            if (Array.isArray(values)) {
                return undefined;
            }
            return true;
        }
        const result = {};
        Object.keys(node).forEach((key) => {
            const child = deriveTouchedFromErrors(node[key], values && values[key]);
            if (child !== undefined) {
                result[key] = child;
            }
        });
        return Object.keys(result).length ? result : undefined;
    }

    return true;
};

const mergeTouched = (current, incoming) => {
    if (incoming === undefined) {
        return current;
    }

    if (incoming === true) {
        return true;
    }

    if (Array.isArray(incoming)) {
        const result = Array.isArray(current) ? current.slice() : [];
        incoming.forEach((value, index) => {
            result[index] = mergeTouched(result[index], value);
        });
        return result;
    }

    if (typeof incoming === 'object') {
        const result = (current && typeof current === 'object' && !Array.isArray(current)) ? {...current} : {};
        Object.keys(incoming).forEach((key) => {
            result[key] = mergeTouched(result[key], incoming[key]);
        });
        return result;
    }

    return current;
};


const ScriptGeneratorClassifier2ChoicesFormContainer = ({formikBag, cancel, handleModalClick, previousPages, items, extraFields, parameters}) => {
    useTouchErrorsOnChange(formikBag);
    const hasErrors = formikBag.errors && Object.keys(formikBag.errors).length > 0;

    return (
        <Form>
            <ModalBody onClick={handleModalClick}>
                <ScriptGeneratorClassifier2ChoicesForm id="choiceScripts"
                    previousPages={previousPages}
                    items={items}
                    extraFields={extraFields}
                    parameters={parameters}
                />
            </ModalBody>
            <ModalFooter onClick={handleModalClick}>
                <button className="btn btn-outline-secondary mr-auto" type="button" onClick={cancel}>
                    <FormattedMessage {...messages.buttons.cancel} />
                </button>
                <button className="btn btn-primary" type="submit"
                    disabled={formikBag.isSubmitting || formikBag.isValidating || hasErrors}>
                    <FormattedMessage {...messages.buttons.ok} />
                </button>
            </ModalFooter>
        </Form>
    );
};

export default injectIntl(ScriptGeneratorClassifier2ChoicesFormContainer);
